import { useMemo } from "react"
import { type Query as CubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import {
  useBudgetRecommenderControllerGetHoldsQuery,
  useBudgetRecommenderControllerGetRowSettingsQuery,
} from "~/ui-rtk/api/budgetRecommenderApi"
import { getPivotData } from "~/ui-rtk/utils/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerGetAllModelsQuery,
  useCustomAttributionControllerGetInitiativeTypesQuery,
  type CustomAttributionModelWithWeights,
} from "~/ui-rtk/api/customAttributionApi"
import { useCompanyControllerGetFinancialsQuery } from "~/ui-rtk/api/companyApi"
import { useGetCampaignsQuery } from "~/ui-rtk/api/paidMediaApi"
import {
  addDays,
  adsetCubeQuery,
  aggregateWindow,
  allocateRows,
  buildBaseRows,
  campaignCubeQuery,
  LAST_UPDATED,
  latestBrandValueDate,
  MAX_DATE_QUERY,
  normalizeCurrentBudget,
  resolveDateAnchors,
  windowStartFor,
  type DailyMeasures,
  type MediaTargetLike,
  type RecentChangeHoldLike,
  type WindowAggregate,
} from "~/shared/budget-recommender/compute"
import { type BudgetInputs, type RecommendationRow } from "../types"
import { confidenceFor } from "../budget-utils"
import {
  guardrailLabel,
  resolveTargetMerDisplay,
  type TargetMerScope,
  type TargetMerType,
} from "~/ui-rtk/utils/spend-guardrail"

// The recommendation math lives in ~/shared/budget-recommender so the API's
// budget_recommend tool computes exactly what this page shows. This hook only
// fetches; re-exported so existing importers of this module keep working.
export {
  addDays,
  aggregateWindow,
  confidenceFor,
  latestBrandValueDate,
  normalizeCurrentBudget,
  windowStartFor,
}
export type { DailyMeasures, WindowAggregate }

// Stable default so the recommendations memo doesn't recompute when callers
// omit the periods map
const EMPTY_PERIODS: ReadonlyMap<string, number> = new Map()

export interface BudgetRecommendationOptions {
  /**
   * The rows the user has CHECKED. Every other categorized row is treated as
   * unchecked: it stays at its current budget and, when active, commits that
   * budget to the allocation pool before the checked rows are scaled (see
   * allocateBudgets). `null`/undefined means no selection has been applied
   * yet — every row is in, the "all-in" recommendation. Only the cheap
   * scaling pass re-runs when this changes.
   */
  selectedIds?: ReadonlySet<string> | null
}

export function useBudgetRecommendations(
  inputs: BudgetInputs,
  periodByEntity: ReadonlyMap<string, number> = EMPTY_PERIODS,
  options: BudgetRecommendationOptions = {},
) {
  const selectedIds = options.selectedIds ?? null
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id ?? ""

  const { data: financials } = useCompanyControllerGetFinancialsQuery(
    { id: companyId },
    { skip: !companyId },
  )

  // Saved row settings: the pins (and step cap overrides) the allocation
  // must honour. A hard dependency like the holds: without it a pinned row
  // would load checked, and the default selection would include it.
  const {
    data: rowSettings,
    isLoading: isLoadingRowSettings,
    error: rowSettingsError,
  } = useBudgetRecommenderControllerGetRowSettingsQuery(undefined, {
    skip: !companyId,
  })

  // Entities a person or agent changed inside the hold window (Financial
  // Settings → manual change hold). Read from the Audit table by the API;
  // the recommender's own moves never appear here. A hard dependency: rows
  // without it carry no recent-change labels, and the review would open
  // without asking for the acknowledgment.
  const {
    data: holdsData,
    isLoading: isLoadingHolds,
    error: holdsError,
  } = useBudgetRecommenderControllerGetHoldsQuery(undefined, {
    skip: !companyId,
  })

  const {
    data: mediaTargets,
    isLoading: isLoadingTargets,
    error: mediaTargetsError,
  } = useCustomAttributionControllerGetAllMediaTargetsQuery()

  const {
    data: metaCampaigns,
    isLoading: isLoadingMetaCampaigns,
    error: metaCampaignsError,
  } = useGetCampaignsQuery({ platform: "meta" }, { skip: !companyId })

  // /paid-media/meta/campaigns 400s with "Meta account not connected" for
  // companies that simply have no Meta connector — an expected state for
  // Google/TikTok-only companies, not a load failure. Treat it as an empty
  // campaign list so their other channels still get recommendations.
  const isMetaNotConnected = useMemo(() => {
    if (!metaCampaignsError) return false
    const err = metaCampaignsError as {
      status?: number | string
      data?: { message?: unknown }
    }
    return (
      err.status === 400 && /not connected/i.test(String(err.data?.message))
    )
  }, [metaCampaignsError])

  const metaCampaignsData = useMemo(
    () => metaCampaigns ?? (isMetaNotConnected ? [] : undefined),
    [metaCampaigns, isMetaNotConnected],
  )

  const { data: tiktokCampaigns, isLoading: isLoadingTiktokCampaigns } =
    useGetCampaignsQuery({ platform: "tiktok" }, { skip: !companyId })

  const { data: googleCampaigns, isLoading: isLoadingGoogleCampaigns } =
    useGetCampaignsQuery({ platform: "google" }, { skip: !companyId })

  const {
    data: attributionModels = [] as CustomAttributionModelWithWeights[],
    isLoading: isLoadingModels,
  } = useCustomAttributionControllerGetAllModelsQuery()

  const { data: detailedTypes = [] } =
    useCustomAttributionControllerGetInitiativeTypesQuery()
  const customTypes = useMemo(
    () => detailedTypes.filter(t => t.isCustom),
    [detailedTypes],
  )

  // Fetch max dates for campaigns and adsets to anchor lookback window
  const maxDateQuery: CubeQuery = MAX_DATE_QUERY

  const {
    resultSet: maxDateResult,
    isLoading: isLoadingMaxDate,
    error: maxDateError,
  } = useCubeQuery(maxDateQuery, { skip: !companyId })

  // Date ranges anchored to n, the newest day with any data (see
  // resolveDateAnchors): the daily fetch window, the fixed 7-day MER window,
  // and the last complete day every row window ends on.
  const {
    fetchDateRange,
    dateRange7,
    latestCompleteDate,
    freshnessUnavailable,
  } = useMemo(() => {
    if (!maxDateResult) {
      return {
        fetchDateRange: null,
        dateRange7: null,
        latestCompleteDate: null,
        freshnessUnavailable: false,
      }
    }
    const row = maxDateResult.tablePivot()[0] ?? {}
    return resolveDateAnchors(
      row[`${LAST_UPDATED}.brand_media_paid_optimization_campaigns_max_date`],
      row[`${LAST_UPDATED}.brand_media_paid_optimization_adsets_max_date`],
      new Date().toISOString().split("T")[0],
    )
  }, [maxDateResult])

  const campaignQuery: CubeQuery = useMemo(
    () => campaignCubeQuery(companyId, fetchDateRange),
    [companyId, fetchDateRange],
  )

  const adsetQuery: CubeQuery = useMemo(
    () => adsetCubeQuery(companyId, fetchDateRange),
    [companyId, fetchDateRange],
  )

  const {
    resultSet: campaignResults,
    isLoading: isLoadingCampaignCube,
    error: campaignCubeError,
  } = useCubeQuery(campaignQuery, { skip: !companyId || !fetchDateRange })

  const {
    resultSet: adsetResults,
    isLoading: isLoadingAdsetCube,
    error: adsetCubeError,
  } = useCubeQuery(adsetQuery, { skip: !companyId || !fetchDateRange })

  // Metrics cube query for MER/aMER performance data (fixed 7-day window —
  // this is a page-level metric independent of per-row analysis periods)
  const CUBE_METRICS = "metrics"
  const merCubeQuery: CubeQuery = useMemo(
    () => ({
      measures: [
        `${CUBE_METRICS}.shopify_total_new_customer_revenue`,
        `${CUBE_METRICS}.shopify_total_customer_revenue`,
        `${CUBE_METRICS}.shopify_ecomm_new_customer_revenue`,
        `${CUBE_METRICS}.shopify_ecomm_total_customer_revenue`,
        `${CUBE_METRICS}.total_direct_spend`,
      ],
      timeDimensions: [
        {
          dimension: `${CUBE_METRICS}.date`,
          dateRange: dateRange7 ?? undefined,
        },
      ],
    }),
    [dateRange7],
  )

  const { resultSet: merResult, isLoading: isLoadingMer } = useCubeQuery(
    merCubeQuery,
    { skip: !companyId || !dateRange7 },
  )

  const targetMerType = (financials?.targetMerType as TargetMerType) ?? "amer"
  const targetMerScope =
    (financials?.targetMerScope as TargetMerScope) ?? "total"
  const targetMerValue = financials?.targetMerValue ?? 0
  // How the customer expresses the guardrail (aMER / MER / CAC). The ratio
  // math below is unchanged; the approval modal converts to CAC for display.
  const targetMerDisplay = resolveTargetMerDisplay(financials)

  const performanceMetrics = useMemo(() => {
    if (!merResult) {
      return {
        actualMer: null as number | null,
        targetMer: targetMerValue,
        merLabel: "",
        targetMerDisplay,
        targetMerScope,
      }
    }

    const rows = merResult.tablePivot()
    if (!rows.length) {
      return {
        actualMer: null as number | null,
        targetMer: targetMerValue,
        merLabel: "",
        targetMerDisplay,
        targetMerScope,
      }
    }

    const revenueMeasures = {
      "amer-total": `${CUBE_METRICS}.shopify_total_new_customer_revenue`,
      "amer-ecomm": `${CUBE_METRICS}.shopify_ecomm_new_customer_revenue`,
      "mer-total": `${CUBE_METRICS}.shopify_total_customer_revenue`,
      "mer-ecomm": `${CUBE_METRICS}.shopify_ecomm_total_customer_revenue`,
    } as const

    const merKey = `${targetMerType}-${targetMerScope}` as const
    const revenueMeasure =
      revenueMeasures[merKey] ?? revenueMeasures["mer-ecomm"]
    const label = guardrailLabel(targetMerType, targetMerScope)

    let totalRevenue = 0
    let totalDirectSpend = 0
    for (const row of rows) {
      totalRevenue += Number(row[revenueMeasure]) || 0
      totalDirectSpend += Number(row[`${CUBE_METRICS}.total_direct_spend`]) || 0
    }

    const actualMer =
      totalDirectSpend > 0 ? totalRevenue / totalDirectSpend : null

    return {
      actualMer,
      targetMer: targetMerValue,
      merLabel: label,
      targetMerDisplay,
      targetMerScope,
    }
  }, [
    merResult,
    targetMerType,
    targetMerScope,
    targetMerValue,
    targetMerDisplay,
  ])

  const brandSpendPct = financials?.targetBrandSpendPercentOfTotal ?? 0.3

  const modelsArray = attributionModels as CustomAttributionModelWithWeights[]

  // Everything up to (not including) the scaling pass. Heavy, so it must
  // not re-run on a checkbox toggle — that only touches allocateRows below.
  const baseRows = useMemo((): RecommendationRow[] => {
    // Wait for all critical data before processing to avoid flash
    if (
      !mediaTargets ||
      !metaCampaignsData ||
      !latestCompleteDate ||
      !holdsData
    ) {
      return []
    }
    return buildBaseRows({
      inputs,
      campaignRows: campaignResults
        ? getPivotData(campaignResults, campaignQuery)
        : [],
      adsetRows: adsetResults ? getPivotData(adsetResults, adsetQuery) : [],
      mediaTargets: mediaTargets as MediaTargetLike[],
      metaCampaigns: metaCampaignsData,
      tiktokCampaigns,
      googleCampaigns,
      models: modelsArray,
      customTypes,
      recentChanges: {
        days: holdsData.days,
        holds: holdsData.holds as RecentChangeHoldLike[],
      },
      periodByEntity,
      latestCompleteDate,
      fetchStart: fetchDateRange?.[0] ?? null,
      nowMs: Date.now(),
    })
  }, [
    campaignResults,
    adsetResults,
    metaCampaignsData,
    tiktokCampaigns,
    googleCampaigns,
    mediaTargets,
    modelsArray,
    campaignQuery,
    adsetQuery,
    inputs,
    latestCompleteDate,
    fetchDateRange,
    periodByEntity,
    customTypes,
    holdsData,
  ])

  // Pins, the brand-search policy and the step cap — applied in the shared
  // scaling pass, so the page and budget_recommend hold the same rows.
  const brandSearchPolicy = financials?.brandSearchPolicy
  const maxBudgetStepPct = financials?.maxBudgetStepPct ?? null
  const rowControls = useMemo(
    () => ({
      settings: rowSettings ?? [],
      brandSearchPolicy,
      maxBudgetStepPct,
      nowMs: Date.now(),
    }),
    [rowSettings, brandSearchPolicy, maxBudgetStepPct],
  )

  // The scaling pass: cheap, and the only thing that re-runs when the user
  // checks or unchecks a row.
  const { rows: recommendations, allocation } = useMemo(
    () =>
      allocateRows(baseRows, inputs, brandSpendPct, selectedIds, rowControls),
    [baseRows, inputs, brandSpendPct, selectedIds, rowControls],
  )

  const isLoading =
    isLoadingMaxDate ||
    isLoadingCampaignCube ||
    isLoadingAdsetCube ||
    isLoadingTargets ||
    isLoadingMetaCampaigns ||
    isLoadingTiktokCampaigns ||
    isLoadingGoogleCampaigns ||
    isLoadingModels ||
    isLoadingMer ||
    isLoadingHolds ||
    isLoadingRowSettings

  // A failed prerequisite must surface as an error, never as rows === []:
  // the table's "no campaigns or ad sets with spend" empty state would
  // misreport a loading failure as missing spend data. Only the queries the
  // recommendation path hard-depends on are listed — tiktok/google campaign
  // budgets and the MER metric degrade gracefully. The RTK entries are gated
  // on data absence: a failed tag-invalidation refetch (e.g. Meta throttling
  // right after a bulk approve) keeps the last-fulfilled data, and computed
  // rows must not vanish behind an error box while that data is still usable.
  // The cube entries need no such gate — @cubejs-client nulls resultSet on
  // error. Meta's expected not-connected 400 is exempted above.
  const loadErrors = useMemo(() => {
    const failed: string[] = []
    if (maxDateError) failed.push("data freshness dates")
    if (campaignCubeError) failed.push("campaign performance metrics")
    if (adsetCubeError) failed.push("ad set performance metrics")
    if (metaCampaignsError && !metaCampaigns && !isMetaNotConnected) {
      failed.push("live Meta campaigns")
    }
    if (mediaTargetsError && !mediaTargets) {
      failed.push("initiative type assignments")
    }
    if (holdsError && !holdsData) failed.push("recent changes")
    if (rowSettingsError && !rowSettings) failed.push("saved row pins")
    return failed
  }, [
    maxDateError,
    campaignCubeError,
    adsetCubeError,
    metaCampaignsError,
    metaCampaigns,
    isMetaNotConnected,
    mediaTargetsError,
    mediaTargets,
    holdsError,
    holdsData,
    rowSettingsError,
    rowSettings,
  ])

  const hasMissingData = recommendations.some(r => r.hasMissingData)
  const hasUncategorizedAdsets = recommendations.some(
    r => r.hasUncategorizedAdsets,
  )
  const labeledCount = recommendations.filter(
    r => (r.recentChanges?.length ?? 0) > 0,
  ).length
  const inactiveCount = recommendations.filter(r => !r.isActive).length
  /** The recent change window (Financial Settings), null until known; 0 = labels off. */
  const recentChangeWindowDays = holdsData?.days ?? null

  return {
    recommendations,
    allocation,
    isLoading,
    hasMissingData,
    hasUncategorizedAdsets,
    labeledCount,
    inactiveCount,
    recentChangeWindowDays,
    brandSpendPct,
    latestCompleteDate,
    freshnessUnavailable,
    loadErrors,
    performanceMetrics,
  }
}
